/**
 * Performance Monitoring Utilities
 *
 * Tracks Core Web Vitals and custom performance metrics using:
 * - web-vitals library for LCP, FID, CLS, FCP, TTFB
 * - Firebase Performance Monitoring for custom traces
 * - Google Analytics for metric reporting
 *
 * @module performanceMonitoring
 */

import { getCLS, getFCP, getFID, getLCP, getTTFB } from 'web-vitals';
import type { Metric } from 'web-vitals';
import { getPerformance, trace as createTrace, FirebasePerformance } from 'firebase/performance';
import { logEvent, isSupported, type Analytics } from 'firebase/analytics';
import { FirebaseApp } from 'firebase/app';
import { initializeFirebase, isFirebaseInitialized, features } from '@watchlist/shared';
import { isNative } from '../capacitor-init';

// Extended metric type to include rating (available in web-vitals v2+)
interface MetricWithRating extends Metric {
  rating?: 'good' | 'needs-improvement' | 'poor';
}

// Performance instance cache
let perfInstance: FirebasePerformance | null = null;
/** True after a successful getPerformance(); avoids re-entering SDK when ready. */
let performanceSdkReady = false;
/**
 * True when getPerformance failed (e.g. Performance Monitoring disabled in Firebase Console).
 * Stops repeated init attempts and noisy logs on every page.
 */
let performanceSdkUnavailable = false;
let performanceInitInFlight: Promise<FirebasePerformance | null> | null = null;

// Active traces map for managing custom traces
const activeTraces = new Map<string, ReturnType<typeof createTrace>>();

// Metrics storage for dashboard
interface StoredMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

const metricsHistory: StoredMetric[] = [];
const MAX_HISTORY = 100;

/**
 * Initialize Firebase Performance Monitoring
 * Safe to call multiple times - will only initialize once
 */
export const initializePerformanceMonitoring = async (): Promise<FirebasePerformance | null> => {
  if (isNative()) {
    console.log('⚡ Performance Monitoring: Skipped on native platform');
    return null;
  }

  if (performanceSdkUnavailable) {
    return null;
  }

  if (performanceSdkReady && perfInstance) {
    return perfInstance;
  }

  if (performanceInitInFlight) {
    return performanceInitInFlight;
  }

  performanceInitInFlight = (async (): Promise<FirebasePerformance | null> => {
    try {
      if (!isFirebaseInitialized()) {
        await initializeFirebase();
      }

      const { firebaseManager } = await import('@watchlist/shared');
      const app: FirebaseApp = await firebaseManager.getApp();

      if (!app) {
        performanceSdkUnavailable = true;
        console.warn('⚡ Performance Monitoring: Firebase app not available');
        return null;
      }

      perfInstance = getPerformance(app);
      performanceSdkReady = true;
      console.log('✅ Performance Monitoring: Initialized successfully');
      return perfInstance;
    } catch (error) {
      performanceSdkUnavailable = true;
      perfInstance = null;
      if (process.env.NODE_ENV === 'development') {
        console.debug(
          'Performance Monitoring unavailable (enable in Firebase Console → Build → Performance Monitoring):',
          error
        );
      }
      return null;
    } finally {
      performanceInitInFlight = null;
    }
  })();

  return performanceInitInFlight;
};

/**
 * Get the rating category for a Web Vital metric
 */
/** True when the global gtag stub exists; avoids logEvent before Firebase wires GA (stops "gtagFunction is not a function"). */
const isGtagCallable = (): boolean => {
  if (typeof window === 'undefined') return false;
  const g = (window as unknown as { gtag?: unknown }).gtag;
  return typeof g === 'function';
};

const getRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
  // Thresholds based on Google's Core Web Vitals recommendations
  const thresholds: Record<string, { good: number; poor: number }> = {
    CLS: { good: 0.1, poor: 0.25 },
    FCP: { good: 1800, poor: 3000 },
    FID: { good: 100, poor: 300 },
    LCP: { good: 2500, poor: 4000 },
    TTFB: { good: 800, poor: 1800 },
    INP: { good: 200, poor: 500 },
  };

  const threshold = thresholds[name];
  if (!threshold) return 'good';

  if (value <= threshold.good) return 'good';
  if (value >= threshold.poor) return 'poor';
  return 'needs-improvement';
};

/**
 * Report a Web Vital metric to Firebase Analytics and Performance
 */
const reportMetric = async (metric: MetricWithRating) => {
  const { name, value, id, delta } = metric;

  // Calculate rating if not provided (web-vitals v2 may not include it)
  const rating = metric.rating || getRating(name, value);

  // Store metric locally for dashboard
  const storedMetric: StoredMetric = {
    name,
    value,
    rating,
    timestamp: Date.now(),
  };

  metricsHistory.push(storedMetric);
  if (metricsHistory.length > MAX_HISTORY) {
    metricsHistory.shift();
  }

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    const color = rating === 'good' ? '🟢' : rating === 'needs-improvement' ? '🟡' : '🔴';
    console.log(`${color} Web Vital [${name}]: ${value.toFixed(2)}ms (${rating})`);
  }

  try {
    // Use Analytics instance from FirebaseManager (after gtag is wired). Avoid getAnalytics(app)
    // before init — it causes "gtagFunction is not a function" when measurementId loads late.
    try {
      const { firebaseManager, isFirebaseInitialized } = await import('@watchlist/shared');
      if (
        features.enableAnalytics &&
        isFirebaseInitialized() &&
        (await isSupported()) &&
        isGtagCallable()
      ) {
        let analytics: Analytics | null = null;
        try {
          analytics = await firebaseManager.getAnalytics();
        } catch {
          analytics = null;
        }
        if (analytics && typeof window !== 'undefined') {
          // Defer until after gtag.js finishes wiring (avoids "gtagFunction is not a function")
          window.setTimeout(() => {
            if (!isGtagCallable()) return;
            try {
              void logEvent(analytics!, 'web_vitals', {
                metric_name: name,
                metric_value: Math.round(value),
                metric_rating: rating,
                metric_id: id,
                metric_delta: Math.round(delta),
              }).catch(() => {
                /* ignore */
              });
            } catch {
              /* ignore */
            }
          }, 1500);
        }
      }
    } catch {
      // Analytics optional — skip until Firebase fully initialized
    }

    // Also create a Firebase Performance custom metric if available
    if (perfInstance) {
      const t = createTrace(perfInstance, `web_vital_${name.toLowerCase()}`);
      t.putMetric('value', Math.round(value));
      t.putAttribute('rating', rating);
      t.start();
      t.stop();
    }

    // Report to Firestore for admin dashboard aggregation
    try {
      const { reportWebVitalToFirestore } = await import('../services/performanceAggregationService');
      await reportWebVitalToFirestore({
        name: name as 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP',
        value,
        rating,
        timestamp: new Date().toISOString(),
      });
    } catch (firestoreError) {
      // Silently fail - don't break the app if Firestore reporting fails
      console.debug('Firestore metric reporting failed:', firestoreError);
    }
  } catch (error) {
    console.error('Failed to report metric:', error);
  }
};

/**
 * Initialize Web Vitals tracking
 * Call this once when the app starts
 */
export const initializeWebVitals = () => {
  // Runs in Capacitor WebView too: metrics go to Analytics like on desktop web.
  // Firebase Performance custom traces stay web-only (see initializePerformanceMonitoring).
  if (isNative()) {
    console.log('⚡ Web Vitals: Initializing (native WebView → Analytics)');
  } else {
    console.log('⚡ Web Vitals: Initializing tracking...');
  }

  // Core Web Vitals
  getCLS(reportMetric);
  getFCP(reportMetric);
  getFID(reportMetric);
  getLCP(reportMetric);
  getTTFB(reportMetric);

  console.log('✅ Web Vitals: Tracking initialized');
};

/**
 * Start a custom performance trace
 * Use this to measure specific operations like page loads or API calls
 *
 * @example
 * const trace = await startTrace('movie_detail_load');
 * // ... do work ...
 * await stopTrace('movie_detail_load');
 */
export const startTrace = async (traceName: string, attributes?: Record<string, string>): Promise<boolean> => {
  try {
    if (performanceSdkUnavailable) {
      return false;
    }

    if (!perfInstance) {
      await initializePerformanceMonitoring();
    }

    if (!perfInstance) {
      return false;
    }

    // Don't start duplicate traces
    if (activeTraces.has(traceName)) {
      console.warn(`Trace '${traceName}' already active`);
      return false;
    }

    const t = createTrace(perfInstance, traceName);

    // Add attributes if provided
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        t.putAttribute(key, value);
      });
    }

    t.start();
    activeTraces.set(traceName, t);

    if (process.env.NODE_ENV === 'development') {
      console.log(`⏱️ Trace started: ${traceName}`);
    }

    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Failed to start trace '${traceName}':`, error);
    }
    return false;
  }
};

/**
 * Stop a custom performance trace and record metrics
 */
export const stopTrace = async (
  traceName: string,
  metrics?: Record<string, number>,
  attributes?: Record<string, string>
): Promise<boolean> => {
  try {
    const t = activeTraces.get(traceName);
    if (!t) {
      // Normal when Performance is disabled — startTrace never registered a trace
      return false;
    }

    // Add metrics if provided
    if (metrics) {
      Object.entries(metrics).forEach(([key, value]) => {
        t.putMetric(key, value);
      });
    }

    // Add additional attributes if provided
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        t.putAttribute(key, value);
      });
    }

    t.stop();
    activeTraces.delete(traceName);

    if (process.env.NODE_ENV === 'development') {
      console.log(`⏱️ Trace stopped: ${traceName}`);
    }

    return true;
  } catch (error) {
    console.error(`Failed to stop trace '${traceName}':`, error);
    return false;
  }
};

/**
 * Record a single metric without a trace
 * Useful for one-off measurements
 */
export const recordMetric = async (
  metricName: string,
  value: number,
  attributes?: Record<string, string>
): Promise<boolean> => {
  try {
    if (performanceSdkUnavailable) {
      return false;
    }

    if (!perfInstance) {
      await initializePerformanceMonitoring();
    }

    if (!perfInstance) {
      return false;
    }

    const t = createTrace(perfInstance, metricName);
    t.putMetric('value', value);

    if (attributes) {
      Object.entries(attributes).forEach(([key, val]) => {
        t.putAttribute(key, val);
      });
    }

    t.start();
    t.stop();

    return true;
  } catch (error) {
    console.error(`Failed to record metric '${metricName}':`, error);
    return false;
  }
};

/**
 * Measure the duration of an async function
 * Automatically starts and stops a trace around the function
 *
 * @example
 * const result = await measureAsync('fetch_movie_details', async () => {
 *   return await fetchMovieDetails(movieId);
 * });
 */
export const measureAsync = async <T>(
  traceName: string,
  fn: () => Promise<T>,
  attributes?: Record<string, string>
): Promise<T> => {
  const startTime = performance.now();
  await startTrace(traceName, attributes);

  try {
    const result = await fn();
    const duration = performance.now() - startTime;

    await stopTrace(traceName, { duration_ms: Math.round(duration) }, {
      success: 'true',
    });

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    await stopTrace(traceName, { duration_ms: Math.round(duration) }, {
      success: 'false',
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    throw error;
  }
};

/**
 * Get recent metrics for the performance dashboard
 */
export const getRecentMetrics = (): StoredMetric[] => {
  return [...metricsHistory];
};

/**
 * Get the latest value for each metric type
 */
export const getLatestMetrics = (): Record<string, StoredMetric | undefined> => {
  const latest: Record<string, StoredMetric | undefined> = {};

  // Get the most recent of each metric type
  for (const metric of metricsHistory) {
    if (!latest[metric.name] || metric.timestamp > latest[metric.name]!.timestamp) {
      latest[metric.name] = metric;
    }
  }

  return latest;
};

/**
 * Clear metrics history (useful for testing)
 */
export const clearMetricsHistory = () => {
  metricsHistory.length = 0;
};

// Pre-defined trace names for consistency
export const TraceNames = {
  // Page loads
  MOVIE_DETAIL_LOAD: 'movie_detail_load',
  /** `/person/:id` profile (PeopleDetail) */
  PERSON_PROFILE_LOAD: 'person_profile_load',
  /** `/person/:id/credits` full filmography */
  PERSON_CREDITS_LOAD: 'person_credits_load',
  WATCHLIST_DETAIL_LOAD: 'watchlist_detail_load',
  SEARCH_PAGE_LOAD: 'search_page_load',
  HOME_PAGE_LOAD: 'home_page_load',
  PROFILE_PAGE_LOAD: 'profile_page_load',

  // Data operations
  SEARCH_MOVIES: 'search_movies',
  SEARCH_WATCHLISTS: 'search_watchlists',
  SEARCH_USERS: 'search_users',
  SEARCH_PEOPLE: 'search_people',
  FETCH_MOVIE_DETAILS: 'fetch_movie_details',
  FETCH_PERSON_CREDITS: 'fetch_person_credits',
  LOAD_WATCHLIST_MOVIES: 'load_watchlist_movies',

  // User actions
  ADD_MOVIE_TO_WATCHLIST: 'add_movie_to_watchlist',
  REMOVE_MOVIE_FROM_WATCHLIST: 'remove_movie_from_watchlist',
  CREATE_WATCHLIST: 'create_watchlist',
  MARK_AS_WATCHED: 'mark_as_watched',

  // Cache operations
  CACHE_HIT: 'cache_hit',
  CACHE_MISS: 'cache_miss',
  FIRESTORE_QUERY: 'firestore_query',
} as const;

export type TraceName = typeof TraceNames[keyof typeof TraceNames];

/** SearchPage sets; MovieDetail reads to report `search_to_movie_open` latency */
export const PERF_SEARCH_TO_MOVIE_START_KEY = 'wl_perf_search_to_movie_start';


